import {
  faAddressBook,
  faPenToSquare,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorCode } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { FolderTabs } from '@/common/FolderTabs';
import { InfoSectionContainer } from '@/common/InfoSection';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { TabsBar } from '@/common/TabsBar';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { DeletePhotoTab } from '@/photo/DeletePhotoTab';
import { UploadPhotoTab } from '@/photo/UploadPhotoTab';
import { TakePhotoTab } from '@/profile/TakePhotoTab';
import { PageContent, PageHeader } from '@/settings/settingsStyles';
import { useProfileHelpPlacement } from '~/hooks/useHelpPlacements';
import { formatString } from '~/utils/stringFormat';

/**
 * The server's upload cap in MB, for the too-large message. The unit is its own
 * catalogue string.
 *
 * The server accepts up to 8 MB, more than legacy's 5,120,000 bytes; the picker
 * still checks the selected file against legacy's limit before cropping.
 */
const MAX_UPLOAD_MB = 8;

type PhotoTab = 'upload' | 'takePhoto' | 'delete';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/profile/change-avatar',
)({
  component: EditProfilePhotoRoute,
});

function EditProfilePhotoRoute() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  // Keyed on the viewer's relation to the displayed member, as legacy's profile
  // screens share one help placement block.
  const placement = useProfileHelpPlacement(memberId);
  const [activeTab, setActiveTab] = useState<PhotoTab>('upload');
  const [status, setStatus] = useState<string | null>(null);

  const memberIdNum = Number.parseInt(memberId, 10);

  const {
    data: member,
    isLoading,
    error,
  } = api.getMemberProfile.useQuery(
    { path: { member_id: memberIdNum } },
    { enabled: Number.isFinite(memberIdNum) },
  );

  const { mutateAsync: uploadAvatar } = api.uploadMemberAvatar.useMutation();
  const { mutateAsync: deleteAvatar } = api.deleteMemberAvatar.useMutation();

  const mainTabs = [
    {
      title: t('actions.edit'),
      to: 'edit',
      icon: faPenToSquare,
    },
    {
      title: t('profile.profile-photo'),
      to: 'change-avatar',
      icon: faAddressBook,
    },
  ];

  const refreshMember = async (): Promise<void> => {
    // The Delete tab's visibility reads the member; the app bar's photo reads
    // `/users/me`, which is the same stored key when the subject is the viewer.
    await Promise.all([
      api.getMemberProfile.invalidateQueries(),
      api.getCurrentUser.invalidateQueries(),
    ]);
  };

  const handleUpload = async (blob: Blob): Promise<void> => {
    await uploadAvatar({
      path: { member_id: memberIdNum },
      body: { file: blob },
    });
    await refreshMember();
    // Legacy's confirmation.
    setStatus(t('profile.your-new-profile-photo'));
  };

  const handleDelete = async (): Promise<void> => {
    await deleteAvatar({ path: { member_id: memberIdNum } });
    // Legacy's confirmation. Both are set before the refresh, which flips
    // `hasPhoto` false and unmounts the Delete pane; the message lives outside
    // the panes, as on the group photo screen.
    setStatus(t('actions.your-profile-photo-was'));
    setActiveTab('upload');
    await refreshMember();
  };

  /**
   * Only `too_large` gets its own message. The other avatar error codes can't
   * be provoked from this screen: the picker rejects files over 5,120,000
   * bytes, the input takes only images, and both panes re-encode through a
   * canvas.
   */
  const uploadErrorMessage = (failure: unknown): string | undefined =>
    errorCode(failure) === 'member_avatar_too_large'
      ? formatString(
          t('groups.photo-too-big'),
          `${MAX_UPLOAD_MB} ${t('wbs.mb')}`,
        )
      : undefined;

  if (isLoading) return <LoadingSpinner centered />;

  /*
   * Changing the photo is self or platform admin only, on the displayed member:
   * a supervisor may edit a student's profile but not their photo, and legacy
   * shows them "Page not found". `can_change_avatar` is the server's answer.
   * Taking the subject from `$memberId` stops an admin overwriting their own
   * photo from another member's page.
   */
  if (error || member === undefined || !member.can_change_avatar) {
    return <NotFoundScreen />;
  }

  /*
   * `has_avatar`, not `Boolean(avatar)`: the server falls back to the default
   * avatar, so `avatar` is never empty.
   */
  const hasPhoto = member.has_avatar;

  const photoTabs: { id: PhotoTab; label: string }[] = [
    { id: 'upload', label: t('wbs.upload') },
    { id: 'takePhoto', label: t('wbs.take-photo') },
    ...(hasPhoto
      ? [{ id: 'delete' as const, label: t('actions.delete') }]
      : []),
  ];

  const handleTabChange = (tab: PhotoTab): void => {
    // A confirmation belongs to the action that produced it, not to the tab the
    // user moves on to.
    setStatus(null);
    setActiveTab(tab);
  };

  return (
    <PageContent>
      <PageHeader>
        <TitleWithQuestion
          title={t('actions.edit-profile')}
          placement={placement}
        />
        <Link to="/members/$memberId/profile/" params={{ memberId }}>
          <WBSButton wide alternate icon={faUser}>
            {t('profile.view-my-profile')}
          </WBSButton>
        </Link>
      </PageHeader>

      <div style={{ marginBottom: 11 }}>
        <TabsBar tabs={mainTabs} base="/members/$memberId/profile" />
      </div>
      <InfoSectionContainer>
        <FormTitle>{t('profile.change-profile-photo')}</FormTitle>

        {status !== null && <AlertBox variant="success">{status}</AlertBox>}
        <AlertBox variant="info">
          {t('profile.your-profile-photo-will')}
        </AlertBox>
        <OptionsContainer>
          <FolderTabs
            tabs={photoTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
          <div style={{ marginTop: '20px' }}>
            {activeTab === 'upload' && (
              <UploadPhotoTab
                // The "300px, and use the delete tab" warning shows only when
                // the member already has a photo, as in legacy.
                showNotice={hasPhoto}
                onUpload={handleUpload}
                resolveErrorMessage={uploadErrorMessage}
              />
            )}
            {activeTab === 'takePhoto' && (
              <TakePhotoTab
                onUpload={handleUpload}
                resolveErrorMessage={uploadErrorMessage}
              />
            )}
            {activeTab === 'delete' && hasPhoto && (
              <DeletePhotoTab onDelete={handleDelete} />
            )}
          </div>
        </OptionsContainer>
      </InfoSectionContainer>
    </PageContent>
  );
}

const OptionsContainer = styled.div`
  border-radius: 5px;
  box-shadow: var(--shadow-md);
  margin-top: 32px;
  padding: 50px 20px 65px;
`;

const FormTitle = styled.h2`
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 22px;
  font-weight: 400;
  letter-spacing: 0.1px;
  margin: 5px 0 26px 0;
`;
